<script>
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import VueEasyLightbox from "vue-easy-lightbox";
import IconZoomGlass from "@js/components/Icons/IconZoomGlass.vue";
import ProductImageLoad from "@js/components/Sections/Product/ProductImageLoad.vue";
import {Swiper, SwiperSlide} from "swiper/vue";

export default {
    name: "BaseImageSlider",
    components: {ProductImageLoad, IconZoomGlass, VueEasyLightbox, IconAngleLeft, Swiper, SwiperSlide},
    props: {images: {type: Array, required: true}},
    data() {
        return {
            is_preview_open: false,
            preview_key: 0,
            swiper: null,
        };
    },
    methods: {
        setSwiper(swiperInstance) {
            this.swiper = swiperInstance;
        },
        onSlideChange(swiperInstance) {
            // Используем realIndex для корректного индекса при loop
            this.preview_key = swiperInstance.realIndex;
            this.$nextTick(() => this.scrollToActive());
        },
        openPreview(key) {
            this.preview_key = key;
            this.is_preview_open = true;
            this.$nextTick(() => this.scrollToActive());
        },
        setSlide(key) {
            if (this.swiper) this.swiper.slideTo(key);
        },
        nextSlide() {
            if (this.swiper) this.swiper.slideNext();
        },
        prevSlide() {
            if (this.swiper) this.swiper.slidePrev();
        },
        nextThumb() {
            this.nextSlide();
        },
        prevThumb() {
            this.prevSlide();
        },
        scrollToActive() {
            const container = this.$refs.thumbsContainer;
            if (!container) return;
            const active = container.children[this.preview_key];
            if (!active) return;
            const containerWidth = container.clientWidth;
            const activeOffset = active.offsetLeft;
            const activeWidth = active.clientWidth;
            const scrollPos = activeOffset - containerWidth / 2 + activeWidth / 2;
            container.scrollTo({left: scrollPos, behavior: 'smooth'});
        }
    }
};
</script>

<template>
    <div v-if="images.length">
        <div id="gallery" class="relative w-full z-1" data-carousel="static">
            <Swiper
                :slides-per-view="1"
                :space-between="30"
                @swiper="setSwiper"
                @slideChange="onSlideChange"
                :loop="true"
                :pagination="{ clickable: true }"
                :autoplay="{ delay: 5000, disableOnInteraction: true }"
                class="relative h-56 overflow-hidden rounded-2xl md:h-96 border border-gray-100 shadow">
                <SwiperSlide
                    v-for="(image, key) in images"
                    :key="key"
                    @click.prevent="openPreview(key)"
                    class="group cursor-pointer"
                    :class="{'hidden': preview_key !== key, 'block': preview_key === key}">
                    <ProductImageLoad :img="image" :img_key="key"/>
                    <div
                        class="max-md:hidden absolute inset-0 p-4 text-white uppercase opacity-0 group-hover:opacity-100 bg-white/50 transition-all">
                        <div class="flex items-center justify-center w-full h-full">
                            <IconZoomGlass/>
                        </div>
                    </div>
                </SwiperSlide>
            </Swiper>
            <button
                v-if="images.length > 1"
                @click.prevent="prevSlide"
                class="group absolute top-0 start-0 z-30 flex items-center justify-center h-full px-4 focus:outline-none">
                <span
                    class="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-white bg-white/30 group-hover:bg-white/50 group-hover:border-baseColor transition">
                    <IconAngleLeft class="w-4 h-4 text-gray-800"/>
                </span>
            </button>
            <button
                v-if="images.length > 1"
                @click.prevent="nextSlide"
                class="group absolute top-0 end-0 z-30 flex items-center justify-center h-full px-4 focus:outline-none">
                <span
                    class="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-white bg-white/30 group-hover:bg-white/50 group-hover:border-baseColor transition">
                    <IconAngleLeft class="w-4 h-4 text-gray-800 rotate-180"/>
                </span>
            </button>
        </div>

        <!-- Горизонтальный список миниатюр -->
        <div class="mt-3 flex items-center" v-if="images.length > 1">
            <button @click.prevent="prevThumb"
                    class="max-md:hidden p-1 h-16 rounded-l-2xl bg-gray-200 md:hover:bg-gray-300 transition">
                <IconAngleLeft class="w-4 h-4 text-gray-600"/>
            </button>
            <div ref="thumbsContainer" class="thumbs-container flex space-x-2 mx-2 p-1">
                <img v-for="(image, idx) in images"
                     :key="idx"
                     :src="image"
                     @click.prevent="setSlide(idx)"
                     class="w-16 h-16 object-cover cursor-pointer rounded-sm transition-transform"
                     :class="preview_key === idx ? 'ring-2 ring-baseColor' : 'opacity-70 md:hover:opacity-100'"
                     :alt="'Картинка' + preview_key"/>
            </div>
            <button @click.prevent="nextThumb"
                    class="max-md:hidden p-1 h-16 rounded-r-2xl bg-gray-200 md:hover:bg-gray-300 transition">
                <IconAngleLeft class="w-4 h-4 text-gray-600 rotate-180"/>
            </button>
        </div>
        <VueEasyLightbox :visible="is_preview_open"
                         :imgs="images"
                         :loop="true"
                         :index="preview_key"
                         @hide="is_preview_open = false"
                         :moveDisabled="true"/>
    </div>
</template>

<style scoped lang="scss">
.thumbs-container {
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.z-1 {
    z-index: 1;
}
</style>
